import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { getOrderDetails, toastError } from "../../api/services";
import useAuth from "../../Features/Utils/useAuth";
import "./OrderConfirmation.css";
import { removeFromCart } from "../../Features/Cart/cartSlice";
import { useDispatch } from "react-redux";

function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orderId = searchParams.get("orderId");
  const transactionId = searchParams.get("transactionId");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giftDiscount, setGiftDiscount] = useState(0);

  useEffect(() => {
    if (!orderId) {
      navigate("/");
      return;
    }

    const fetchOrder = async () => {
      try {
        const response = await getOrderDetails(orderId, user?.id);
        if (response?.data?.success) {
          const orderData = response.data.data;
          
          // Calculate or get gift discount if available
          const appliedGiftDiscount = orderData.giftDiscount || 
                                      orderData.appliedGiftDiscount || 
                                      orderData.gift_card_discount || 0;
          setGiftDiscount(appliedGiftDiscount);
          
          // Fix shipping charge - calculate if missing
          if (!orderData.shippingCharge || orderData.shippingCharge === 0) {
            const originalTotal = Number(orderData.originalTotal) || 0;
            const totalDiscount = Number(orderData.totalDiscount) || 0;
            const subtotal = originalTotal - totalDiscount;
            // Free shipping above ₹500, else ₹60
            orderData.shippingCharge = subtotal > 500 ? 0 : 60;
          }
          
          // Fix total amount if shipping is missing
          if (orderData.totalAmount && orderData.shippingCharge > 0) {
            const originalTotal = Number(orderData.originalTotal) || 0;
            const totalDiscount = Number(orderData.totalDiscount) || 0;
            const platformFee = Number(orderData.platformFee) || 0;
            const subtotal = originalTotal - totalDiscount;
            const correctTotal = subtotal + orderData.shippingCharge + platformFee;
            if (orderData.totalAmount !== correctTotal) {
              orderData.totalAmount = correctTotal;
            }
          }
          
          setOrder(orderData);
          
          // Clear cart items
          if (orderData.items && orderData.items.length > 0) {
            orderData.items.forEach((item) => {
              dispatch(
                removeFromCart({
                  productID: item.productID,
                  variantID: item.variantID,
                })
              );
            });
          }
        } else {
          toastError("Failed to fetch order details");
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching order:", error);
        toastError("Something went wrong");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate, user?.id, dispatch]);

  const getEstimatedDelivery = () => {
    const today = new Date();
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + 5);
    return deliveryDate.toLocaleDateString("en-IN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Safe number conversion
  const toNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Calculate totals with proper shipping logic
  const calculateTotals = () => {
    if (!order) return { subtotal: 0, total: 0, shipping: 0, platformFee: 0, giftDiscount: 0 };
    
    const originalTotal = toNumber(order.originalTotal);
    const totalDiscount = toNumber(order.totalDiscount);
    let shippingCharge = toNumber(order.shippingCharge);
    const platformFee = toNumber(order.platformFee);
    const giftDiscountValue = toNumber(giftDiscount);
    
    // Calculate subtotal
    const subtotal = originalTotal - totalDiscount;
    
    // Calculate shipping if missing (Free above ₹500)
    if (shippingCharge === 0) {
      shippingCharge = subtotal > 500 ? 0 : 60;
    }
    
    // Calculate totals
    const totalWithShipping = subtotal + shippingCharge;
    const totalWithPlatform = totalWithShipping + platformFee;
    const finalTotal = Math.max(totalWithPlatform - giftDiscountValue, 0);
    
    return {
      originalTotal,
      totalDiscount,
      subtotal,
      shippingCharge,
      platformFee,
      giftDiscount: giftDiscountValue,
      finalTotal
    };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="order-confirmation loading">
        <div className="spinner-large"></div>
        <p>Confirming your order...</p>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="order-confirmation">
      <div className="confirmation-container">
        <div className="success-header">
          <div className="success-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <h1>Order Confirmed!</h1>
          <p>Thank you for shopping with us</p>
          <div className="order-id-box">
            <span>Order ID: </span>
            <strong>#{order.id || orderId}</strong>
          </div>
          {transactionId && (
            <div className="transaction-id">
              <i className="fas fa-receipt"></i> Transaction ID: {transactionId}
            </div>
          )}
        </div>

        <div className="delivery-info">
          <div className="info-card">
            <i className="fas fa-truck"></i>
            <div>
              <h4>Estimated Delivery</h4>
              <p>{getEstimatedDelivery()}</p>
            </div>
          </div>
          <div className="info-card">
            <i className="fas fa-map-marker-alt"></i>
            <div>
              <h4>Shipping Address</h4>
              <p>
                {order.address?.full_address || "Address will be updated soon"}
              </p>
            </div>
          </div>
          <div className="info-card">
            <i className="fas fa-credit-card"></i>
            <div>
              <h4>Payment Method</h4>
              <p>
                {order.payment_method === "cod" 
                  ? "Cash on Delivery" 
                  : order.payment_method === "online" 
                  ? "Online Payment" 
                  : order.payment_method || "Cash on Delivery"}
              </p>
              <p className="payment-status">
                Status: <span className={order.payment_status === "paid" ? "paid" : "pending"}>
                  {order.payment_status === "paid" ? "Paid" : "Pending"}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="order-summary-section">
          <h3>Order Summary</h3>
          <div className="items-list">
            {order.items && order.items.length > 0 ? (
              order.items.map((item, index) => (
                <div className="order-item" key={index}>
                  <img
                    src={item.frontImg || item.image}
                    alt={item.productName}
                    onError={(e) => {
                      e.target.src = "/placeholder-image.jpg";
                    }}
                  />
                  <div className="item-details">
                    <h4>{item.productName}</h4>
                    <p>Quantity: {item.quantity}</p>
                    <p className="item-price">
                      ₹{(item.price || item.productPrice || 0) * item.quantity}
                    </p>
                    {item.discount > 0 && (
                      <p className="item-discount">
                        <i className="fas fa-tag"></i> Saved: ₹{(item.discount || item.discount_amount || 0) * item.quantity}
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-items">No items found</p>
            )}
          </div>

          <div className="price-breakdown">
            <div className="price-row">
              <span>Total MRP</span>
              <span>₹{totals.originalTotal.toFixed(2)}</span>
            </div>
            <div className="price-row discount">
              <span>Product Discounts</span>
              <span>-₹{totals.totalDiscount.toFixed(2)}</span>
            </div>
            <div className="price-row">
              <span>Subtotal</span>
              <span>₹{totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="price-row">
              <span>Shipping & Handling</span>
              <span>₹{totals.shippingCharge.toFixed(2)}</span>
              {totals.shippingCharge === 0 && totals.subtotal > 500 && (
                <span className="free-shipping-badge">Free Shipping</span>
              )}
              {totals.shippingCharge > 0 && totals.subtotal <= 500 && (
                <span className="shipping-note">(Free above ₹500)</span>
              )}
            </div>
            {totals.platformFee > 0 && (
              <div className="price-row">
                <span>Platform Fee</span>
                <span>₹{totals.platformFee.toFixed(2)}</span>
              </div>
            )}
            {totals.giftDiscount > 0 && (
              <div className="price-row discount">
                <span>Gift Card Discount</span>
                <span>-₹{totals.giftDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="price-row total">
              <span>Total Amount</span>
              <span>₹{totals.finalTotal.toFixed(2)}</span>
            </div>
          </div>

          {order.payment_method === "cod" && (
            <div className="cod-note">
              <i className="fas fa-info-circle"></i>
              Pay ₹{totals.finalTotal.toFixed(2)} in cash when your order arrives
            </div>
          )}
        </div>

        <div className="action-buttons">
          <Link to="/order" className="btn-primary">
            <i className="fas fa-list"></i> View All Orders
          </Link>
          <Link to="/" className="btn-secondary">
            <i className="fas fa-shopping-cart"></i> Continue Shopping
          </Link>
        </div>

        <div className="whatsapp-support">
          <a
            href="https://wa.me/1234567890"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-whatsapp"></i> Need help? Chat with us
          </a>
        </div>
      </div>

      <style>{`
        .order-confirmation {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .confirmation-container {
          background: white;
          border-radius: 24px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          overflow: hidden;
        }
        
        .success-header {
          text-align: center;
          padding: 48px 24px 32px;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border-bottom: 1px solid #eef2f6;
        }
        
        .success-icon {
          font-size: 4rem;
          color: #2e7d32;
          margin-bottom: 16px;
        }
        
        .success-icon i {
          background: #e8f5e9;
          border-radius: 50%;
          padding: 20px;
        }
        
        .success-header h1 {
          font-size: 2rem;
          margin: 0 0 8px;
          color: #1a1c1e;
        }
        
        .success-header p {
          color: #666;
          margin-bottom: 20px;
        }
        
        .order-id-box {
          background: #f5f5f5;
          display: inline-block;
          padding: 10px 20px;
          border-radius: 40px;
          font-size: 14px;
          margin-top: 10px;
        }
        
        .transaction-id {
          margin-top: 12px;
          font-size: 12px;
          color: #666;
        }
        
        .delivery-info {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          padding: 32px;
          background: #fafbfe;
          border-bottom: 1px solid #eef2f6;
        }
        
        .info-card {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .info-card i {
          font-size: 1.8rem;
          color: #facc15;
        }
        
        .info-card h4 {
          margin: 0 0 4px;
          font-size: 14px;
          color: #666;
        }
        
        .info-card p {
          margin: 0;
          font-size: 14px;
          font-weight: 500;
          color: #1a1c1e;
        }
        
        .payment-status {
          margin-top: 5px !important;
          font-size: 12px !important;
        }
        
        .payment-status .paid {
          color: #2e7d32;
          font-weight: 600;
        }
        
        .payment-status .pending {
          color: #ed6c02;
          font-weight: 600;
        }
        
        .order-summary-section {
          padding: 32px;
        }
        
        .order-summary-section h3 {
          margin: 0 0 24px;
          font-size: 1.3rem;
        }
        
        .items-list {
          margin-bottom: 32px;
        }
        
        .order-item {
          display: flex;
          gap: 16px;
          padding: 16px;
          border: 1px solid #eef2f6;
          border-radius: 16px;
          margin-bottom: 12px;
        }
        
        .order-item img {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 12px;
        }
        
        .item-details {
          flex: 1;
        }
        
        .item-details h4 {
          margin: 0 0 8px;
          font-size: 16px;
        }
        
        .item-details p {
          margin: 4px 0;
          font-size: 13px;
          color: #666;
        }
        
        .item-price {
          font-weight: 600;
          color: #1a1c1e !important;
        }
        
        .item-discount {
          color: #2e7d32 !important;
          font-size: 12px;
        }
        
        .price-breakdown {
          background: #f8f9fa;
          padding: 24px;
          border-radius: 20px;
          max-width: 400px;
          margin-left: auto;
        }
        
        .price-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          font-size: 14px;
          color: #555;
        }
        
        .price-row.discount {
          color: #2e7d32;
        }
        
        .price-row.total {
          font-size: 18px;
          font-weight: 700;
          border-top: 2px solid #e0e0e0;
          margin-top: 8px;
          padding-top: 16px;
          color: #1a1c1e;
        }
        
        .free-shipping-badge {
          background: #e8f5e9;
          color: #2e7d32;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 20px;
          margin-left: 8px;
        }
        
        .shipping-note {
          font-size: 10px;
          color: #999;
          margin-left: 8px;
        }
        
        .cod-note {
          background: #fff3e0;
          padding: 12px 16px;
          border-radius: 12px;
          margin-top: 20px;
          font-size: 13px;
          color: #e65100;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .action-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          padding: 32px;
          background: #fafbfe;
          border-top: 1px solid #eef2f6;
        }
        
        .btn-primary, .btn-secondary {
          padding: 12px 24px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        
        .btn-primary {
          background: #facc15;
          color: #1a1c1e;
        }
        
        .btn-primary:hover {
          background: #e5b800;
          transform: translateY(-2px);
        }
        
        .btn-secondary {
          background: white;
          color: #1a1c1e;
          border: 1px solid #ddd;
        }
        
        .btn-secondary:hover {
          background: #f5f5f5;
        }
        
        .whatsapp-support {
          text-align: center;
          padding: 24px;
          background: #25D36610;
        }
        
        .whatsapp-support a {
          color: #25D366;
          text-decoration: none;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        
        .whatsapp-support a:hover {
          text-decoration: underline;
        }
        
        .loading {
          text-align: center;
          padding: 60px;
        }
        
        .spinner-large {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #facc15;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .no-items {
          text-align: center;
          padding: 40px;
          color: #666;
        }
        
        @media (max-width: 768px) {
          .order-confirmation { padding: 20px 16px; }
          .delivery-info { grid-template-columns: 1fr; }
          .action-buttons { flex-direction: column; }
          .btn-primary, .btn-secondary { justify-content: center; }
          .price-breakdown { max-width: 100%; }
        }
      `}</style>
    </div>
  );
}

export default OrderConfirmation;